import { useEffect, useRef, useState } from "react";

type Row = Record<string, any>;
export function PortalAccessPanel({accounts,api,csrf}: {
  accounts: Row[];
  api: <T>(path: string, options?: {method?: string;body?: unknown;csrf?: string | undefined}) => Promise<T>;
  csrf?: string | undefined;
}) {
  const [account, setAccount] = useState("");
  const [records, setRecords] = useState<{users: Row[];invitations: Row[]} | null>(null);
  const [action, setAction] = useState<{path:string;title:string;email:string} | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [link, setLink] = useState("");
  const [busy, setBusy] = useState(false);
  const generation = useRef(0);
  async function load(id: string, version: number) {
    const result = await api<{users:Row[];invitations:Row[]}>(`/admin/accounts/${id}/access`);
    if (generation.current === version) setRecords(result);
  }
  useEffect(() => {
    const version = ++generation.current;
    setRecords(null); setAction(null); setLink(""); setError(""); setNotice(""); setBusy(false);
    if (account) void load(account, version).catch(cause => {if (generation.current === version) setError(cause.message);});
    return () => {generation.current++;};
  }, [account,api]);
  return <section className="cr-panel">
    <h2>Manage account access</h2>
    <p>Remove an account holder, cancel an invitation, or issue a one-use password recovery link after independently confirming their identity.</p>
    <label className="cr-field">Customer account<select value={account} onChange={e => setAccount(e.target.value)}><option value="">Select an account</option>{accounts.map(row => <option key={row.id} value={row.id}>{row.name} · {row.account_number}</option>)}</select></label>
    {error && <p role="alert">{error}</p>}{notice && <p role="status">{notice}</p>}
    {account && !records && !error && <p role="status">Loading account access…</p>}
    {records && <>
      {!records.users.length && <p>No account holders have accepted access.</p>}
      {records.users.map(user => <div className="cr-panel" key={user.id}>
        <h3>{user.display_name}</h3><p>{user.email} · {user.revoked_at ? "Access removed" : user.disabled_at ? "Login disabled" : "Account holder"}</p>
        {!user.revoked_at && <div className="cr-actions">
          {!user.disabled_at && <button className="cr-button secondary" disabled={busy} onClick={() => {setLink("");setAction({path:`access/${user.id}/recovery`,title:"Create recovery link",email:user.email});}}>Create recovery link</button>}
          <button className="cr-button secondary" disabled={busy} onClick={() => {setLink("");setAction({path:`access/${user.id}/revoke`,title:"Remove account access",email:user.email});}}>Remove account access</button>
        </div>}
      </div>)}
      <h3>Pending invitations</h3>{!records.invitations.length && <p>No invitations awaiting acceptance.</p>}
      {records.invitations.map(invite => <div className="cr-panel" key={invite.id}><p>{invite.email} · expires {new Date(invite.expires_at).toLocaleString()}</p><button className="cr-button secondary" disabled={busy} onClick={() => {setLink("");setAction({path:`invitations/${invite.id}/revoke`,title:"Cancel invitation",email:invite.email});}}>Cancel invitation</button></div>)}
    </>}
    {action && <form className="cr-form" key={action.path} onSubmit={async e => {
      e.preventDefault(); const values = new FormData(e.currentTarget), version = generation.current;
      setBusy(true);setError("");setNotice("");setLink("");
      try {
        const result = await api<{recoveryUrl?:string}>(`/admin/accounts/${account}/${action.path}`,{method:"POST",csrf,body:{reason:values.get("reason"),identityConfirmed:values.get("confirmed")==="on"}});
        if (version !== generation.current) return;
        setLink(result.recoveryUrl ?? "");setAction(null);setNotice(result.recoveryUrl ? "Recovery link created. Deliver it privately after confirming the customer's identity. It expires in 30 minutes." : "Account access updated.");
        await load(account,version);
      } catch(cause) {if(version===generation.current)setError((cause as Error).message);}
      finally {if(version===generation.current)setBusy(false);}
    }}>
      <h3>{action.title}: {action.email}</h3>
      <label className="cr-field">Reason and identity verification notes<textarea name="reason" required minLength={20} maxLength={1000} rows={3}/></label>
      <label><input type="checkbox" name="confirmed" required/> I independently verified the customer and this account-access change.</label>
      <p>Removing access affects only this account. Password recovery changes the holder's login password and signs them out of all accounts.</p>
      <div className="cr-actions"><button className="cr-button" disabled={busy}>{busy ? "Saving…" : action.title}</button><button type="button" className="cr-button secondary" disabled={busy} onClick={() => setAction(null)}>Cancel</button></div>
    </form>}
    {link && <label className="cr-field">Private recovery link · one use · 30 minutes<input readOnly value={link} onFocus={e => e.currentTarget.select()}/></label>}
  </section>;
}
